import { ChevronLeft, ChevronRight } from "lucide-react";
import styles from "./MessageActions.module.css";

interface BranchNavProps {
	/** 0-based position of the active reply among its siblings. */
	currentIndex: number;
	/** Total sibling replies in this branch group (always > 1 when rendered). */
	totalSiblings: number;
	/** Called with the target 0-based index when the user clicks a chevron. */
	onNavigate: (index: number) => void;
	/** Disable while a switch (or another turn) is in flight. */
	isDisabled?: boolean;
}

/**
 * Message-branching switcher: ‹ 2/3 › under an assistant reply that has
 * alternative regenerated siblings. Left chevron steps to the older sibling,
 * right to the newer; both clamp at the ends. Purely presentational — the
 * parent owns the switch call + reload.
 */
export function BranchNav({
	currentIndex,
	totalSiblings,
	onNavigate,
	isDisabled,
}: BranchNavProps) {
	const atStart = currentIndex <= 0;
	const atEnd = currentIndex >= totalSiblings - 1;

	return (
		<div className={styles.branchNav} role="group" aria-label="Reply versions">
			<button
				type="button"
				className={styles.branchBtn}
				onClick={() => onNavigate(currentIndex - 1)}
				disabled={isDisabled || atStart}
				title="Previous version"
				aria-label="Previous reply version"
			>
				<ChevronLeft size={14} />
			</button>
			<span className={styles.branchCount} aria-live="polite">
				{currentIndex + 1}/{totalSiblings}
			</span>
			<button
				type="button"
				className={styles.branchBtn}
				onClick={() => onNavigate(currentIndex + 1)}
				disabled={isDisabled || atEnd}
				title="Next version"
				aria-label="Next reply version"
			>
				<ChevronRight size={14} />
			</button>
		</div>
	);
}
